import type { Layer, RasterLayer } from "./Layer";

/**
 * Recursively find a layer by id, descending into groups.
 * Shared utility — avoids duplicating this pattern across 7+ files.
 */
export function findLayer(layers: readonly Layer[], id: string): Layer | null {
  for (const l of layers) {
    if (l.id === id) return l;
    if (l.kind === "group") {
      const inner = findLayer(l.children, id);
      if (inner) return inner;
    }
  }
  return null;
}

/**
 * Recursively find a raster layer by id, descending into groups.
 * Returns null if the layer doesn't exist or isn't a raster layer.
 *
 * Also matches a `kind: "background"` layer (#i165) — Photoshop's locked
 * Background layer is structurally compatible with `RasterLayer` for paint
 * tools that go through this shared lookup (same `pixels` buffer and
 * `LayerBase` fields; it just lacks a `mask`, which is a valid state for
 * `RasterLayer.mask` too). The return type stays `RasterLayer | null` on
 * purpose — widening it to a union would ripple into every one of this
 * function's ~30 call sites, most of which are outside this fix's scope.
 * Callers that need to special-case Background behavior (locked
 * position/image, "erase paints bg color instead of transparency", …)
 * should check `layer.kind` themselves; see `brush.ts`'s `eraseToColor`
 * stamp option for the erase-specific behavior this enables.
 */
export function findRaster(layers: readonly Layer[], id: string): RasterLayer | null {
  for (const l of layers) {
    if (l.id === id && (l.kind === "raster" || l.kind === "background")) {
      return l as RasterLayer;
    }
    if (l.kind === "group") {
      const inner = findRaster(l.children, id);
      if (inner) return inner;
    }
  }
  return null;
}

/**
 * Recursively collect all raster layers (flattened).
 */
export function collectRasterLayers(layers: readonly Layer[]): RasterLayer[] {
  const result: RasterLayer[] = [];
  const walk = (ls: readonly Layer[]) => {
    for (const l of ls) {
      if (l.kind === "raster") result.push(l);
      if (l.kind === "group") walk(l.children);
    }
  };
  walk(layers);
  return result;
}
